import { create } from 'zustand';

export interface Toast {
  id: number;
  kind: 'success' | 'error' | 'info';
  message: string;
  action?: { label: string; run(): void };
}

interface ToastState {
  toasts: Toast[];
  push(toast: Omit<Toast, 'id'>): void;
  dismiss(id: number): void;
}

let seq = 0;

/** UI state: transient notifications. Not server state, not auth. */
export const useToasts = create<ToastState>((set, get) => ({
  toasts: [],
  push: (toast) => {
    const id = ++seq;
    set({ toasts: [...get().toasts.slice(-2), { ...toast, id }] });
    window.setTimeout(() => get().dismiss(id), toast.kind === 'error' ? 7000 : 4000);
  },
  dismiss: (id) => set({ toasts: get().toasts.filter((t) => t.id !== id) }),
}));

export const toast = {
  success: (message: string, action?: Toast['action']) =>
    useToasts.getState().push({ kind: 'success', message, action }),
  error: (message: string, action?: Toast['action']) =>
    useToasts.getState().push({ kind: 'error', message, action }),
  info: (message: string, action?: Toast['action']) =>
    useToasts.getState().push({ kind: 'info', message, action }),
};

/**
 * Open modal dialogs, innermost last. A modal <dialog> sits in the browser's top layer and
 * makes the rest of the page inert, so the toaster renders inside the innermost one to stay
 * visible (and reachable) above it.
 */
export const useModalHosts = create<{ hosts: HTMLElement[] }>(() => ({ hosts: [] }));

export function registerModalHost(el: HTMLElement): () => void {
  useModalHosts.setState((s) => ({ hosts: [...s.hosts.filter((h) => h !== el), el] }));
  return () => useModalHosts.setState((s) => ({ hosts: s.hosts.filter((h) => h !== el) }));
}
